import { useState, useEffect } from 'react';
import { 
  Wallet, ArrowDownLeft, ArrowUpRight, PieChart, Activity, History,
  TrendingUp, Shield, Coins, CreditCard, Clock, RefreshCw
} from 'lucide-react';
import BackButton from '../components/BackButton';
import DepositTab from '../components/wallet/DepositTab';
import WithdrawalTab from '../components/wallet/WithdrawalTab';
import PortfolioTab from '../components/wallet/PortfolioTab';
import WalletActivityView from '../components/wallet/WalletActivityView';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const WalletNew = () => {
  const [activeTab, setActiveTab] = useState('portfolio');
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchBalance = async () => {
    try {
      const response = await api.get('/wallet/balance');
      setBalance(response.data?.balance || 0);
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [activeTab]);

  // Action cards configuration
  const actionCards = [
    { 
      id: 'portfolio', 
      label: 'Portfolio', 
      icon: PieChart, 
      bgColor: 'from-purple-600 to-indigo-600',
      borderColor: 'border-purple-500/40',
      iconBg: 'bg-purple-500/30',
      textColor: 'text-purple-400'
    },
    { 
      id: 'deposit', 
      label: 'Deposit', 
      icon: ArrowDownLeft, 
      bgColor: 'from-green-600 to-emerald-600',
      borderColor: 'border-green-500/40',
      iconBg: 'bg-green-500/30',
      textColor: 'text-green-400'
    },
    { 
      id: 'withdraw', 
      label: 'Withdraw', 
      icon: ArrowUpRight, 
      bgColor: 'from-orange-600 to-red-600',
      borderColor: 'border-orange-500/40',
      iconBg: 'bg-orange-500/30',
      textColor: 'text-orange-400'
    },
    { 
      id: 'active', 
      label: 'Active', 
      icon: Clock, 
      bgColor: 'from-blue-600 to-cyan-600',
      borderColor: 'border-blue-500/40',
      iconBg: 'bg-blue-500/30',
      textColor: 'text-blue-400'
    },
  ];

  const getActiveCardInfo = () => {
    if (activeTab === 'history') {
      return { label: 'Transaction History', icon: History, bgColor: 'from-pink-600 to-rose-600' };
    }
    return actionCards.find(c => c.id === activeTab) || actionCards[0];
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5 animate-fade-in">
      <BackButton label="Back" />
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="p-2.5 bg-gradient-to-br from-purple-600/30 to-indigo-600/30 rounded-xl border border-purple-500/20">
          <Wallet className="h-6 w-6 text-purple-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Wallet</h1>
          <p className="text-gray-400 text-xs">Manage your funds and transactions</p>
        </div>
      </div>

      {/* Balance Card */}
      <div className="glow-card p-4 rounded-xl bg-gradient-to-br from-gray-900/80 to-gray-800/50 border border-purple-500/20">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-lg">
            <Coins className="h-7 w-7 text-amber-400" />
          </div>
          <div>
            <p className="text-xs text-gray-400 flex items-center">
              <Shield className="h-3 w-3 mr-1" />
              Total Balance
            </p>
            <p className="text-2xl font-bold bg-gradient-to-r from-amber-200 to-yellow-400 bg-clip-text text-transparent">
              {loading ? (
                <span className="animate-pulse text-gray-400">Loading...</span>
              ) : (
                `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Action Cards - 4 Column Horizontal Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
        {actionCards.map((card) => {
          const Icon = card.icon;
          const isActive = activeTab === card.id;
          return (
            <button
              key={card.id}
              onClick={() => setActiveTab(card.id)}
              style={{ padding: '12px 8px' }}
              className={`rounded-xl transition-all duration-200 ${
                isActive
                  ? `bg-gradient-to-br ${card.bgColor}`
                  : `bg-gray-900/70 border ${card.borderColor}`
              }`}
            >
              <div className="flex flex-col items-center space-y-1">
                <div className={`p-2 rounded-lg ${isActive ? 'bg-white/20' : card.iconBg}`}>
                  <Icon className={`h-5 w-5 ${isActive ? 'text-white' : card.textColor}`} />
                </div>
                <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>
                  {card.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Transaction History Button - Full Width */}
      <button
        onClick={() => setActiveTab('history')}
        className={`w-full p-3 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 ${
          activeTab === 'history'
            ? 'bg-gradient-to-r from-pink-600 to-rose-600'
            : 'bg-gray-900/70 border border-pink-500/40'
        }`}
      >
        <div className={`p-1.5 rounded-lg ${activeTab === 'history' ? 'bg-white/20' : 'bg-pink-500/30'}`}>
          <History className={`h-4 w-4 ${activeTab === 'history' ? 'text-white' : 'text-pink-400'}`} />
        </div>
        <span className={`text-sm font-medium ${activeTab === 'history' ? 'text-white' : 'text-gray-300'}`}>
          Transaction History
        </span>
      </button>

      {/* Active Tab Indicator Bar */}
      <div className={`h-1 rounded-full bg-gradient-to-r ${
        activeTab === 'portfolio' ? 'from-purple-500 to-indigo-500' :
        activeTab === 'deposit' ? 'from-green-500 to-emerald-500' :
        activeTab === 'withdraw' ? 'from-orange-500 to-red-500' :
        activeTab === 'active' ? 'from-blue-500 to-cyan-500' :
        'from-pink-500 to-rose-500'
      }`} />

      {/* Tab Content */}
      <div className="animate-slide-up">
        <div className="glow-card rounded-xl p-4 border border-gray-800/50">
          {/* Tab Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              {(() => {
                const info = getActiveCardInfo();
                const Icon = info.icon;
                return (
                  <>
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${info.bgColor}`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-white">{info.label}</h2>
                  </>
                );
              })()}
            </div>
            <button 
              onClick={fetchBalance}
              className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          {activeTab === 'portfolio' && <PortfolioTab />}
          {activeTab === 'deposit' && <DepositTab />}
          {activeTab === 'withdraw' && <WithdrawalTab balance={balance} onSuccess={fetchBalance} />}
          {activeTab === 'active' && <WalletActivityView type="active" />}
          {activeTab === 'history' && <WalletActivityView type="history" />}
        </div>
      </div>

      {/* Quick Info Cards - 2x2 Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
        <div className="glow-card p-3 rounded-xl bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-500/20">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <ArrowDownLeft className="h-4 w-4 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Deposits</p>
              <p className="text-sm font-bold text-green-400">Available</p>
            </div>
          </div>
        </div>
        
        <div className="glow-card p-3 rounded-xl bg-gradient-to-br from-orange-900/20 to-red-900/20 border border-orange-500/20">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <ArrowUpRight className="h-4 w-4 text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Withdrawals</p>
              <p className="text-sm font-bold text-orange-400">24/7</p>
            </div>
          </div>
        </div>
        
        <div className="glow-card p-3 rounded-xl bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-500/20">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <TrendingUp className="h-4 w-4 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Trading</p>
              <p className="text-sm font-bold text-blue-400">Active</p>
            </div>
          </div>
        </div>
        
        <div className="glow-card p-3 rounded-xl bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/20">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <CreditCard className="h-4 w-4 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Security</p>
              <p className="text-sm font-bold text-purple-400">Secured</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletNew;
